import { Box, Chip, Dialog, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Trans, useTranslation } from 'react-i18next';

import COLORS from '@/resources/style/Themes';
import WebAppLogoBlue from '@/resources/images/WebAppLogoBlue.svg';
import { StringKeys } from '@/resources/strings/StringKeys';
import {
  getTierChipColors,
  getTierChipSx,
  type TierChipVariant,
} from '@/utils/entitlements/tierChipStyles';

export type TierLearnMoreVariant = TierChipVariant;

const CONNECT_GRADIENT =
  'linear-gradient(to right, #ffffff 28.213%, #f0f0f0 61.681%, #dedede 100%)';
const OPS_GRADIENT =
  'linear-gradient(to right, #ffffff 26.758%, #f4f8ff 63.622%, #e7f1ff 100%)';
const RESPOND_GRADIENT =
  'linear-gradient(to right, #ffffff 33.064%, #f6fff8 66.047%, #f2fff5 100%)';

const DESCRIPTION_COLOR = '#4e4e4e';
const DIVIDER_COLOR = '#908f8f';

type TierLearnMoreConfig = {
  chipLabel: string;
  gradient: string;
  description: string;
  includedTitle: string;
  includedItems: string[];
};

const TIER_CONFIG: Record<TierLearnMoreVariant, TierLearnMoreConfig> = {
  connect: {
    chipLabel: 'Connect',
    gradient: CONNECT_GRADIENT,
    description: StringKeys.TIER_CONNECT_DESCRIPTION,
    includedTitle: StringKeys.TIER_CONNECT_INCLUDED_TITLE,
    includedItems: [
      StringKeys.TIER_CONNECT_INCLUDED_ITEM_1,
      StringKeys.TIER_CONNECT_INCLUDED_ITEM_2,
      StringKeys.TIER_CONNECT_INCLUDED_ITEM_3,
      StringKeys.TIER_CONNECT_INCLUDED_ITEM_4,
      StringKeys.TIER_CONNECT_INCLUDED_ITEM_5,
    ],
  },
  ops: {
    chipLabel: 'Ops',
    gradient: OPS_GRADIENT,
    description: StringKeys.TIER_OPS_DESCRIPTION,
    includedTitle: StringKeys.TIER_OPS_INCLUDED_TITLE,
    includedItems: [
      StringKeys.TIER_OPS_INCLUDED_ITEM_1,
      StringKeys.TIER_OPS_INCLUDED_ITEM_2,
      StringKeys.TIER_OPS_INCLUDED_ITEM_3,
      StringKeys.TIER_OPS_INCLUDED_ITEM_4,
    ],
  },
  respond: {
    chipLabel: 'Respond',
    gradient: RESPOND_GRADIENT,
    description: StringKeys.TIER_RESPOND_DESCRIPTION,
    includedTitle: StringKeys.TIER_RESPOND_INCLUDED_TITLE,
    includedItems: [
      StringKeys.TIER_RESPOND_INCLUDED_ITEM_1,
      StringKeys.TIER_RESPOND_INCLUDED_ITEM_2,
      StringKeys.TIER_RESPOND_INCLUDED_ITEM_3,
      StringKeys.TIER_RESPOND_INCLUDED_ITEM_4,
      StringKeys.TIER_RESPOND_INCLUDED_ITEM_5,
    ],
  },
};

const BulletDot = ({
  outerColor,
  innerColor,
}: {
  outerColor: string;
  innerColor: string;
}) => (
  <Box
    sx={{
      flexShrink: 0,
      position: 'relative',
      width: 14,
      height: 14,
      borderRadius: '50%',
      backgroundColor: outerColor,
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 5,
        height: 5,
        borderRadius: '50%',
        backgroundColor: innerColor,
      }}
    />
  </Box>
);

type TierLearnMoreModalProps = {
  open: boolean;
  onClose: () => void;
  variant: TierLearnMoreVariant;
};

const TierLearnMoreModal = ({
  open,
  onClose,
  variant,
}: TierLearnMoreModalProps) => {
  const { t } = useTranslation();
  const config = TIER_CONFIG[variant];
  const chipColors = getTierChipColors(variant);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            background: config.gradient,
            borderRadius: 4,
            overflow: 'hidden',
            m: { xs: 2, sm: 3 },
          },
        },
      }}
    >
      <Box sx={{ position: 'relative', py: { xs: 3, sm: 4 } }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: COLORS.GREY,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ px: { xs: 2.5, sm: 2.5 }, mb: 2, }}
        >
          <Box
            component="img"
            src={WebAppLogoBlue}
            alt="Navigator"
            sx={{ height: 46, width: 197, objectFit: 'contain', flexShrink: 0,}}
          />
          <Box
            sx={{
              width: '1px',
              height: 16,
              backgroundColor: DIVIDER_COLOR,
              flexShrink: 0,
            }}
          />
          <Chip
            label={config.chipLabel}
            size="small"
            sx={getTierChipSx(variant)}
          />
        </Stack>

        <Typography
          sx={{
            px: { xs: 2.5, sm: 4 },
            fontSize: 14,
            lineHeight: '28px',
            letterSpacing: '0.2px',
            color: DESCRIPTION_COLOR,
            mb: 2,
          }}
        >
          <Trans
            i18nKey={config.description}
            components={{
              question: (
                <Box
                  component="span"
                  sx={{ fontWeight: 500, color: COLORS.SAFEGUARD_BLUE }}
                />
              ),
            }}
          />
        </Typography>

        <Typography
          sx={{
            px: { xs: 2.5, sm: 4 },
            fontSize: 14,
            fontWeight: 500,
            color: COLORS.SAFEGUARD_BLUE,
            mb: 2,
          }}
        >
          {t(config.includedTitle)}
        </Typography>

        <Stack spacing={1.5} sx={{ px: { xs: 3, sm: 5 } }}>
          {config.includedItems.map((item) => (
            <Stack
              key={item}
              direction="row"
              spacing={1.5}
              alignItems="center"
            >
              <BulletDot
                outerColor={chipColors.backgroundColor}
                innerColor={chipColors.color}
              />
              <Typography sx={{ fontSize: 14, color: DESCRIPTION_COLOR }}>
                {t(item)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Dialog>
  );
};

export default TierLearnMoreModal;
